import { FileUpload, RadioList, TextInput } from "@milestone/component-library";
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import {
  Container,
  Header,
  Section,
  TopSaveWrapper,
} from "../formComponents.styles";
import {
  BlueButton,
  OutlineButton,
  SaveButtonContainer,
  StyledForm,
} from "../../Admin.styles";
import { useHistory, useParams } from "react-router-dom";
import {
  makeUndefined,
  useMemberDetails,
} from "../../../../services/hooks/MemberDetails";
import {
  DocumentName,
  Employee,
  EmployeeSchema,
  YesOrNo,
} from "./employeeInfoSchema";
import {
  EmployeeInfo as EmployeeInformation,
  EmployeeTitle,
  Vehicle,
} from "../../../../services/api/types/Members";
import { Api } from "../../../../services/api";
import { Company } from "../../../../services/api/types/Companies";
import { PhonePrefix } from "../PersonalInfo/personalInfo";
import { DocumentType, UserPermissions } from "../../../../enums";
import { handleAxiosError } from "../../../../services/utils/handleAxiosError";
import { useAppSelector } from "../../../../store/hooks";
import { DateSelectWrapper } from "../formComponents";

const defaultEmployeeInfo: Employee = {
  company: "",
  companyId: "",
  title: "",
  associateId: "",
  [DocumentType.EMPLOYMENT_CONTRACT]: undefined,
  [DocumentType.POLICIES_ACKNOWLEDGEMENTS]: undefined,
  workVisaRequirement: "",
  workVisaType: "",
  workVisaOther: "",
  workVisaExpirationDate: undefined,
  carYear: undefined,
  carMake: "",
  carModel: "",
  carLicensePlate: "",
  driversLicenseExpiration: undefined,
  [DocumentType.STATE_LICENSE]: undefined,
  [DocumentType.CERTIFICATIONS]: undefined,
};

export const EmployeeInfo: React.FC<{}> = () => {
  const history = useHistory();
  const userPermissions = useAppSelector(state => state.auth.user.permissions);
  const SaveButtons = (
    <SaveButtonContainer>
      <BlueButton type="submit">Save</BlueButton>
      <OutlineButton
        type="button"
        onClick={() => history.push("/admin/member")}
      >
        Cancel
      </OutlineButton>
    </SaveButtonContainer>
  );

  const [employeeInfo, setEmployeeInfo] =
    useState<Employee>(defaultEmployeeInfo);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [titles, setTitles] = useState(new Map<string, EmployeeTitle>());
  const [companies, setCompanies] = useState(new Map<string, Company>());
  const { memberId } = useParams<{ memberId: string }>();
  const memberDetails = useMemberDetails(memberId);

  const updateData = async (memberTitle: string, oldData: Employee) => {
    if (memberDetails) {
      const newData = oldData;
      //set the data for employee
      if (memberDetails.employmentInfo) {
        newData.title = memberTitle;
        if (memberDetails.employmentInfo.workVisaRequired !== undefined)
          newData.workVisaRequirement =
            YesOrNo[memberDetails.employmentInfo.workVisaRequired ? 0 : 1];
        if (memberDetails.employmentInfo.workVisaExpirationDate !== undefined) {
          newData.workVisaExpirationDate = new Date(
            memberDetails.employmentInfo.workVisaExpirationDate
          );
        }
        if (
          memberDetails.employmentInfo.driversLicenseExpiration !== undefined
        ) {
          newData.driversLicenseExpiration = new Date(
            memberDetails.employmentInfo.driversLicenseExpiration
          );
        }
        if (
          memberDetails.employmentInfo.vehicles &&
          memberDetails.employmentInfo.vehicles.length
        ) {
          const vehicle = memberDetails.employmentInfo.vehicles[0];
          newData.carMake = vehicle.vehicleMake;
          newData.carModel = vehicle.vehicleModel;
          newData.carYear = vehicle.vehicleYear;
          newData.carLicensePlate = vehicle.licensePlate;
        }
        if (memberDetails.employmentInfo.documents) {
          //grab all of the documents out of employee info
          for (const doc of memberDetails.employmentInfo.documents) {
            const name = doc.documentFileName || "";
            const docType = doc.documentType || "";
            if (doc.documentType) {
              //create empty blob for temp file
              let file: File | Blob = new File([], name);
              if (
                userPermissions.includes(
                  UserPermissions.MemberDownloadDocuments
                )
              ) {
                //download file is has permissions
                file = await Api.members.getEmploymentDocument(
                  memberId,
                  docType,
                  name
                );
              }
              newData[doc.documentType as DocumentName] = new Map([
                [name, file],
              ]);
            }
          }
        }
      }
      newData.associateId = memberDetails.associateId;
      //console.log(memberDetails.companyName);
      newData.company = memberDetails.companyName || "";
      setEmployeeInfo(newData);
      setDataLoaded(true);
    }
  };

  const getData = async () => {
    let memberTitle = "";
    if (memberDetails) {
      // load titles
      if (!titles.size) {
        const res = await Api.members.getTitles();
        const titles = new Map<string, EmployeeTitle>();
        //build titles map and find the member's title
        for (const title of res.data) {
          titles.set(title.name, title);
          if (
            memberDetails.employmentInfo &&
            memberDetails.employmentInfo.employeeTitleId === title.id
          ) {
            memberTitle = title.name;
          }
        }
        setTitles(titles);
      }
      // load companies
      if (!companies.size) {
        try {
          const res = await Api.companies.list();
          const companies = new Map<string, Company>();
          for (const company of res.data.companies) {
            companies.set(company.name, company);
          }
          setCompanies(companies);
        } catch (error) {
          console.error("Companies get:", error);
          handleAxiosError(error);
        }
      }
      // set form fields
      updateData(memberTitle, employeeInfo);
    }
  };

  useEffect(() => {
    getData();
  }, [memberDetails]);

  const submitValues = async (values: Employee) => {
    const member = memberDetails;

    let vehicles: Array<Vehicle> = [];
    if (
      memberDetails &&
      memberDetails.employmentInfo &&
      memberDetails.employmentInfo.vehicles
    ) {
      vehicles = memberDetails.employmentInfo.vehicles;
    }

    if (member) {
      if (member.member.phone && !member.member.phone.startsWith(PhonePrefix)) {
        member.member.phone = PhonePrefix + member.member.phone;
      }
      if (!member.employmentInfo) member.employmentInfo = {};
      const title = titles.get(values.title || "");
      member.employmentInfo.employeeTitle = title ? title.id : "";
      member.associateId = values.associateId || "";
      const vehicle: Vehicle = { id: vehicles.length ? vehicles[0].id : "" };
      vehicle.vehicleMake = values.carMake;
      vehicle.vehicleModel = values.carModel;
      vehicle.vehicleYear = values.carYear;
      vehicle.licensePlate = values.carLicensePlate;
      makeUndefined(vehicle);
      //if any vehicle information add it
      if (
        vehicle.vehicleMake ||
        vehicle.vehicleModel ||
        vehicle.vehicleYear ||
        vehicle.licensePlate
      ) {
        if (
          member.employmentInfo.vehicles &&
          member.employmentInfo.vehicles.length
        ) {
          member.employmentInfo.vehicles[0] = vehicle;
        } else {
          member.employmentInfo.vehicles = [vehicle];
        }
      }
      member.associateId = values.associateId || "";
      const company = companies.get(values.company || "");
      if (company) {
        member.companyId = company.id;
      }
      makeUndefined(member);
      try {
        await Api.members.updateMemberDetails(memberId, member);
        //history.push(`/admin/members/${memberId}/checkIns`);
      } catch (error) {
        console.error("Update Team:", error);
      }
    }
    const title = titles.get(values.title || "");
    const data: EmployeeInformation = {
      employeeTitleId: title ? title.id : "",
      vehicleMake: values.carMake || "",
      vehicleModel: values.carModel || "",
      vehicleYear: values.carYear || 0,
      vehicleId: vehicles.length ? vehicles[0].id : "",
      workVisaRequired: values.workVisaRequirement === "Yes",
      workVisaExpirationDate:
        values.workVisaExpirationDate instanceof Date
          ? values.workVisaExpirationDate.toISOString()
          : values.workVisaExpirationDate,
      licensePlate: values.carLicensePlate || "",
      driversLicenseExpiration:
        values.driversLicenseExpiration instanceof Date
          ? values.driversLicenseExpiration.toISOString()
          : values.driversLicenseExpiration,
      [DocumentType.CERTIFICATIONS]: values[DocumentType.CERTIFICATIONS]
        ? (values[DocumentType.CERTIFICATIONS] as Map<string, File>)
            .values()
            .next().value
        : undefined,
      [DocumentType.EMPLOYMENT_CONTRACT]: values[
        DocumentType.EMPLOYMENT_CONTRACT
      ]
        ? (values[DocumentType.EMPLOYMENT_CONTRACT] as Map<string, File>)
            .values()
            .next().value
        : undefined,
      [DocumentType.POLICIES_ACKNOWLEDGEMENTS]: values[
        DocumentType.POLICIES_ACKNOWLEDGEMENTS
      ]
        ? (values[DocumentType.POLICIES_ACKNOWLEDGEMENTS] as Map<string, File>)
            .values()
            .next().value
        : undefined,
      [DocumentType.STATE_LICENSE]: values[DocumentType.STATE_LICENSE]
        ? (values[DocumentType.STATE_LICENSE] as Map<string, File>)
            .values()
            .next().value
        : undefined,
    };
    try {
      await Api.members.updateEmployeeInfo(memberId, data);
      history.push(`/admin/members/${memberId}/checkIns`);
    } catch (error) {
      console.error("Update Team:", error);
    }
  };

  return (
    <Container>
      {dataLoaded && (
        <Formik
          initialValues={employeeInfo}
          validationSchema={EmployeeSchema}
          onSubmit={submitValues}
        >
          {({ values }) => (
            <StyledForm>
              <TopSaveWrapper>{SaveButtons}</TopSaveWrapper>

              <Section>
                <Header>Company</Header>
                <TextInput
                  name="company"
                  placeholder="No Company Set"
                  options={[...companies.keys()]}
                />
              </Section>

              <Section>
                <Header>Team Member Position</Header>
                <TextInput
                  name="title"
                  placeholder="Find Position"
                  options={[...titles.keys()]}
                />
              </Section>

              <Section>
                <Header>Associate Id</Header>
                <TextInput name="associateId" placeholder="Associate Id" />
              </Section>

              <Section>
                <Header>Team Member Contract</Header>
                <FileUpload
                  name={DocumentType.EMPLOYMENT_CONTRACT}
                  maxNumberOfFiles={1}
                />
              </Section>

              <Section>
                <Header>Policies Acknowledgements</Header>
                <FileUpload
                  name={DocumentType.POLICIES_ACKNOWLEDGEMENTS}
                  maxNumberOfFiles={1}
                />
              </Section>

              <Section>
                <Header>Car Information</Header>
                <TextInput name="carYear" label="Year" placeholder="Year" />
                <TextInput name="carMake" label="Make" placeholder="Make" />
                <TextInput name="carModel" label="Model" placeholder="Model" />
                <TextInput
                  name="carLicensePlate"
                  label="License Plate"
                  placeholder="License Plate"
                />
              </Section>

              <Section>
                <Header>VISA & Work requirements</Header>
                <RadioList
                  label="Work VISA Required?"
                  name="workVisaRequirement"
                  options={YesOrNo}
                />
                {values.workVisaRequirement === "Yes" && (
                  <DateSelectWrapper
                    label="Work VISA Expiration Date"
                    name="workVisaExpirationDate"
                    monthDropdown={true}
                    yearDropdown={true}
                  />
                )}
              </Section>

              <Section>
                <Header>Driver's License Expiration Date</Header>
                <DateSelectWrapper
                  name="driversLicenseExpiration"
                  monthDropdown={true}
                  yearDropdown={true}
                />
              </Section>

              <Section>
                <Header>State License</Header>
                <FileUpload
                  name={DocumentType.STATE_LICENSE}
                  maxNumberOfFiles={1}
                />
              </Section>

              <Section>
                <Header>Certifications</Header>
                <FileUpload name={DocumentType.CERTIFICATIONS} />
              </Section>

              {SaveButtons}
            </StyledForm>
          )}
        </Formik>
      )}
    </Container>
  );
};
